/**
 * Lightweight, dependency-free D-pad/arrow-key spatial navigation for TV
 * remote control use. Only engages on browsing pages (Home/Movies/Series) -
 * the TV Guide (live TV) and Watch pages already have their own, more
 * specific arrow-key schemes (channel-surfing, seek/volume - see
 * VideoPlayer.js/Guide.js/WatchPage.js's own keydown handlers) that this
 * must not fight with.
 *
 * No library/registration step needed: candidates are found by querying the
 * DOM at keypress time (FOCUSABLE_SELECTOR), so newly-rendered items (lazy-
 * loaded batches, search results, expanded groups) are navigable immediately
 * without wiring anything up per render.
 */
(function () {
    const NAV_ENABLED_PAGES = new Set(['home', 'movies', 'series']);

    const FOCUSABLE_SELECTOR = [
        '.nav-link', '.channel-tile', '.dashboard-card', '.movie-card', '.series-card'
    ].join(',');

    function isNavActive() {
        return NAV_ENABLED_PAGES.has(window.app?.currentPage);
    }

    function isTypingContext(el) {
        return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
    }

    function getCandidates(exclude) {
        return Array.from(document.querySelectorAll(FOCUSABLE_SELECTOR))
            .filter(el => el !== exclude)
            .map(el => ({ el, rect: el.getBoundingClientRect() }))
            .filter(({ rect }) => rect.width > 0 && rect.height > 0);
    }

    function rectCenter(rect) {
        return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
    }

    /**
     * Standard TV-nav heuristic: a candidate must actually lie in the
     * requested direction from the current position, scored by primary-axis
     * distance plus a heavily-weighted perpendicular-axis misalignment
     * penalty - this is what keeps Down/Up moving straight down/up through a
     * grid column instead of jumping sideways to a closer-but-wrong-row card.
     */
    function findBestCandidate(fromRect, candidates, direction) {
        const fromCenter = rectCenter(fromRect);
        let best = null;
        let bestScore = Infinity;

        for (const { el, rect } of candidates) {
            const center = rectCenter(rect);
            let inDirection, primary, perpendicular;

            switch (direction) {
                case 'ArrowRight':
                    inDirection = rect.left >= fromRect.right - 1;
                    primary = rect.left - fromRect.right;
                    perpendicular = Math.abs(center.y - fromCenter.y);
                    break;
                case 'ArrowLeft':
                    inDirection = rect.right <= fromRect.left + 1;
                    primary = fromRect.left - rect.right;
                    perpendicular = Math.abs(center.y - fromCenter.y);
                    break;
                case 'ArrowDown':
                    inDirection = rect.top >= fromRect.bottom - 1;
                    primary = rect.top - fromRect.bottom;
                    perpendicular = Math.abs(center.x - fromCenter.x);
                    break;
                case 'ArrowUp':
                    inDirection = rect.bottom <= fromRect.top + 1;
                    primary = fromRect.top - rect.bottom;
                    perpendicular = Math.abs(center.x - fromCenter.x);
                    break;
            }

            if (!inDirection) continue;
            const score = Math.max(primary, 0) + perpendicular * 2;
            if (score < bestScore) {
                bestScore = score;
                best = el;
            }
        }
        return best;
    }

    function moveFocus(direction) {
        const active = document.activeElement;
        const hasActiveTarget = active && active !== document.body && active.matches(FOCUSABLE_SELECTOR);
        const fromRect = hasActiveTarget
            ? active.getBoundingClientRect()
            : { left: 0, right: 0, top: 0, bottom: 0, width: 0, height: 0 }; // nothing focused yet - navigate in from the top-left

        const candidates = getCandidates(hasActiveTarget ? active : null);
        const best = findBestCandidate(fromRect, candidates, direction);
        if (best) {
            best.focus({ preventScroll: true });
            best.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        }
    }

    document.addEventListener('keydown', (e) => {
        if (!isNavActive()) return;
        if (isTypingContext(document.activeElement)) return;

        if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            e.preventDefault();
            moveFocus(e.key);
        } else if (e.key === 'Enter' || e.key === ' ') {
            // <a class="nav-link"> already activates on Enter natively; only
            // our custom, non-native-interactive card <div>s need this. Both
            // Enter and Space, matching standard button/interactive-widget
            // keyboard behavior (Space alone was previously unhandled - a
            // real gap for switch-access/some assistive tech that only
            // sends Space, not Enter).
            const active = document.activeElement;
            if (active && active.matches('.channel-tile, .dashboard-card, .movie-card, .series-card')) {
                e.preventDefault(); // also stops Space from scrolling the page
                active.click();
            }
        }
    });
})();
